import cn from 'classnames'
import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { RequestStatus } from 'legacy/lut/configs/service'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getUrlParam, getUrlWithParams } from 'core-components/helpers/url'
import { useNavigate } from 'core/hooks/useNavigate'
import { TileBoard } from 'partner360/components/tileBoard'
import {
  useWidgetTourActionHandler,
  useWidgetTourHostVisibility,
  WidgetTourAction,
  WidgetTourProvider,
} from 'partner360/components/widgetTour'
import { MANAGER_COCKPIT_PAGE_URL } from 'partner360/configs'
import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'
import { BusinessPriorities } from 'partner360/features/businessPriorities'
import { CockpitUserType } from 'partner360/features/financialCockpit/api/types'
import { invalidateFinancialCockpitData } from 'partner360/features/financialCockpit/model/financialCockpitSlice'
import { selectFinancialCockpitState } from 'partner360/features/financialCockpit/model/selectors'
import { MarketYoyGrowthExpectation } from 'partner360/features/marketYoyGrowthExpectation'
import { CoverageReport } from 'partner360/features/partnersCoverage'
import { StrategicInitiatives } from 'partner360/features/strategicInitiatives'
import { TopPartnersManagement } from 'partner360/features/topPartnersManagement'
import { useWelcomeFlowApi } from 'partner360/features/welcomeFlow/model/hooks/useWelcomeFlowApi'
import { selectIsFetching as selectIsFetchingMyPartners } from 'partner360/features/welcomeFlow/model/selectors/selectIsFetching'
import { selectWelcomeFlowPartners } from 'partner360/features/welcomeFlow/model/selectors/selectWelcomeFlowPartners'
import { useWidgetTour } from 'partner360/features/widgetTour'
import type { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'
import type { TReportRtmType } from 'partner360/models/report/TReportRtmType'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import { MANAGER_COCKPIT_TOUR_TRANSLATION_KEYS } from '../../../configs/managerCockpitTourTranslations'
import { COCKPIT_UNIFIED_FILTER_PANEL_ENABLED } from '../../../constants'
import { setCockpitInitiativesView, setCockpitRevenueAndSobView } from '../../../model'
import { useFilteredCockpitPartnerData } from '../../../model/hooks'
import {
  selectEffectiveCockpitFYear,
  selectFinancialCockpitDataRefreshVersion,
  selectIsFinancialCockpitLoading,
} from '../../../model/selectors'
import { requestFinancialCockpitData } from '../../../model/thunks'
import { useAttentionTileTone } from '../../cockpitAttentionStatuses'
import type { TDocumentType } from '../../cockpitDocumentsAndLinks/types'
import { CockpitFilterPanel } from '../../cockpitFilterPanel/CockpitFilterPanel'
import { CockpitUnifiedFilterPanel } from '../../cockpitFilterPanel/CockpitUnifiedFilterPanel'
import { useInitCockpitFYear } from '../../cockpitFiscalYear/hooks/useInitCockpitFYear'
import { CockpitRtm } from '../../cockpitRtm/CockpitRtm'
import { ManagerCockpitScopePanel } from '../../cockpitScopePanel'
import { CockpitExportProvider } from '../../components/cockpitExport'
import { ManagerCockpitMyPartners } from '../../managerCockpitMyPartners'
import {
  ManagerPlanningCockpitSwitcher,
  type TManagerPlanningCockpitSection,
} from '../../managerPlanningCockpitSwitcher'
import { useManagerPlanningCockpitSections } from '../../managerPlanningCockpitSwitcher/hooks/useManagerPlanningCockpitSections'
import { PlanningCockpitContentWrapper } from '../../planningCockpitContentWrapper'

import { SECTION_URL_PARAM, SECTION_URL_VALUES } from './config'
import { ManagerCockpitNoFilteredData } from './ManagerCockpitNoFilteredData'
import { buildTiles, MANAGER_COCKPIT_ROW_SIZES, MANAGER_COCKPIT_TILE_IDS } from './tiles'
import { MANAGER_COCKPIT_TOURS, MANAGER_COCKPIT_WIDGET_ID } from './tour/config'
import { useClearManagerCockpitTab } from './useClearManagerCockpitTab'

import styles from './ManagerPlanningCockpit.module.scss'

type TTileViewAction =
  | ReturnType<typeof setCockpitRevenueAndSobView>
  | ReturnType<typeof setCockpitInitiativesView>

/** Which sub-view a tour step may open each tile onto. Only these tiles have sub-views, and a
 *  view a tile does not have leaves the step skipped rather than landing somewhere unrelated. */
const TILE_VIEW_SETTERS: Record<string, Record<string, TTileViewAction | undefined> | undefined> = {
  [MANAGER_COCKPIT_TILE_IDS.revenueAndSob]: {
    detail: setCockpitRevenueAndSobView('detail'),
    quality: setCockpitRevenueAndSobView('quality'),
  },
  [MANAGER_COCKPIT_TILE_IDS.initiatives]: {
    initiatives: setCockpitInitiativesView('initiatives'),
    mdf: setCockpitInitiativesView('mdf'),
    quality: setCockpitInitiativesView('quality'),
  },
}

const ManagerPlanningCockpitBody = () => {
  useClearManagerCockpitTab()

  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isManagerCockpitActive = getUrlParam('page', pathname) === MANAGER_COCKPIT_PAGE_URL
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const dataRefreshVersion = useSelector(selectFinancialCockpitDataRefreshVersion)
  const isCockpitLoading = useSelector(selectIsFinancialCockpitLoading)
  const isMyPartnersLoading = useSelector(selectIsFetchingMyPartners)
  const myPartnersCount = useSelector(selectWelcomeFlowPartners).length
  const { fetchData: fetchMyPartners } = useWelcomeFlowApi()
  const isFYearResolved = useInitCockpitFYear('manager', true)
  const filteredPartnerData = useFilteredCockpitPartnerData()
  const attentionTone = useAttentionTileTone(true)
  const [documentsType, setDocumentsType] = useState<TDocumentType>()
  const tiles = useMemo(
    () => buildTiles(attentionTone, { documentsType, onDocumentsTypeChange: setDocumentsType }),
    [attentionTone, documentsType]
  )

  const [tourHostElement, setTourHostElement] = useState<HTMLDivElement | null>(null)
  const isTourHostVisible = useWidgetTourHostVisibility(tourHostElement)
  const [expandedTileId, setExpandedTileId] = useState<string | null>(null)
  const [isSidebarHidden, setIsSidebarHidden] = useState(false)
  const sidebarId = useId()

  const sections = useManagerPlanningCockpitSections()
  const urlSection = getUrlParam(SECTION_URL_PARAM, pathname)

  const selectedSection = useMemo(
    () => sections.find((section) => SECTION_URL_VALUES[section] === urlSection) ?? 'cockpit',
    [sections, urlSection]
  )

  const setSelectedSection = useCallback(
    (section: TManagerPlanningCockpitSection) => {
      if (section === selectedSection) {
        return
      }

      navigate(getUrlWithParams({ [SECTION_URL_PARAM]: SECTION_URL_VALUES[section] }))
    },
    [navigate, selectedSection]
  )

  const {
    partnerData,
    partnerFlags,
    partnerFlagRequests,
    partnerFlagApprovers,
    users,
    fetchingStatus,
  } = useSelector(selectFinancialCockpitState)
  const hasNoFilteredData =
    fetchingStatus === RequestStatus.FULFILLED && filteredPartnerData.length === 0

  const cockpitPartners = useMemo<IPartnerWithFlagRequests[]>(() => {
    const requestsByPartnerId = new Map(
      partnerFlagRequests.map((request) => [request.PartnerId, request])
    )

    return partnerData.map((partner) => ({
      DatasetId: partner.EntryId ?? 0,
      Country: partner.Country,
      CountryCode: partner.CountryCode,
      GeoMarket: partner.GeoMarket,
      GeoRegion: partner.GeoRegion,
      PartnerId: partner.PartnerId,
      LocationId: partner.LocationId,
      PartnerName: partner.PartnerName,
      PartnerRanks: partner.PartnerFlags,
      GeoMarketOrder: 0,
      GlobalPartnerName: partner.GlobalPartnerName ?? '',
      GlobalPartnerId: partner.GlobalPartnerId ?? '',
      IsGlobal: partner.IsGlobal,
      AmplifyMembership: partner.AmplifyTrackWithMembership,
      BitClass: partner.BitClass,
      ...requestsByPartnerId.get(partner.PartnerId),
      Coverage: partner.Coverage,
      // Cockpit calls resellers Commercial; flag definitions use Reseller.
      RTM: partner.RTM === PartnerType.Commercial ? 'Reseller' : (partner.RTM as TReportRtmType),
    }))
  }, [partnerData, partnerFlagRequests])

  const [topPartners, setTopPartners] = useState(cockpitPartners)

  useEffect(() => {
    setTopPartners(cockpitPartners)
  }, [cockpitPartners])

  const pbmAssignments = useMemo(
    () =>
      users
        .filter((user) => user.UserType === CockpitUserType.PBM)
        .map((user) => ({
          PartnerId: user.PartnerId,
          ProgramType: user.Offering,
          PbmEmail: user.Email,
        })),
    [users]
  )

  const loadData = useCallback(
    ({ keepOnScreen = false }: { keepOnScreen?: boolean } = {}) => {
      if (!fYear || !isFYearResolved || !isManagerCockpitActive) {
        return false
      }

      dispatch(requestFinancialCockpitData({ fYear, keepOnScreen, silent: keepOnScreen }))

      return true
    },
    [dispatch, fYear, isFYearResolved, isManagerCockpitActive]
  )
  const reloadData = useCallback(() => {
    loadData()
  }, [loadData])

  /* The year last loaded. Another year loads with the board kept on screen: the tile opened over
     the whole screen and the deck over it stay, and their figures move to the new year's. */
  const loadedFYearRef = useRef<number | null>(null)

  useEffect(() => {
    const isYearSwitch = loadedFYearRef.current !== null && loadedFYearRef.current !== fYear

    if (loadData({ keepOnScreen: isYearSwitch })) {
      loadedFYearRef.current = fYear
    }
  }, [dataRefreshVersion, fYear, loadData])

  useEffect(() => {
    if (sections.includes('myPartners')) {
      fetchMyPartners()
    }
  }, [fetchMyPartners, sections])

  const handleMyPartnersChanged = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  // The board unmounts with its section and with an empty filter result. Its expansion lives up
  // here for the tour, so without the reset it would come back with a tile still open.
  const isBoardShown = selectedSection === 'cockpit' && !hasNoFilteredData

  useEffect(() => {
    if (!isBoardShown) {
      setExpandedTileId(null)
    }
  }, [isBoardShown])

  const handleTourAction = useCallback(
    (action: WidgetTourAction) => {
      switch (action.type) {
        case 'SET_TAB': {
          const section = sections.find((item) => item === action.payload)

          if (!section) {
            return false
          }

          setSelectedSection(section)
          return true
        }
        case 'EXPAND_TILE': {
          if (!tiles.some((tile) => tile.id === action.payload)) {
            return false
          }

          setExpandedTileId(action.payload)

          // The sub-view follows the expansion, in the same order as on the partner board.
          if (action.view) {
            const setView = TILE_VIEW_SETTERS[action.payload]?.[action.view]

            if (!setView) {
              return false
            }

            dispatch(setView)
          }

          return true
        }
        case 'COLLAPSE_TILE': {
          setExpandedTileId(null)
          return true
        }
        default:
          return false
      }
    },
    [dispatch, sections, setSelectedSection, tiles]
  )

  useWidgetTourActionHandler(MANAGER_COCKPIT_WIDGET_ID, handleTourAction)

  const {
    hasTours,
    isLoading: isTourLoading,
    isTourActive,
    startTour,
  } = useWidgetTour(MANAGER_COCKPIT_WIDGET_ID, MANAGER_COCKPIT_TOURS, {
    isReady: !isCockpitLoading && !isMyPartnersLoading,
    // The tour walks the Cockpit section, so it does not start over another one or over an empty
    // filter result; it waits until there is a board to show.
    isAutoStartAllowed: isTourHostVisible && isBoardShown,
  })

  // The tour points at the filters, so it walks them in place. With nothing
  // left to show, the filters are the way out and the tray holding the toggle is gone, so the
  // column stays too.
  const isSidebarFolded = isSidebarHidden && !isTourActive && !hasNoFilteredData

  return (
    <PlanningCockpitContentWrapper
      isBlurContent={false}
      isAdditionalLoading={isMyPartnersLoading || !isFYearResolved}
      reloadData={reloadData}
    >
      {/* The tour host is the content itself: the wrapper unmounts it while the cockpit or My
          Partners loads, which holds auto-start back until there is something to spotlight. */}
      <div
        ref={setTourHostElement}
        className={cn(styles.page, { [styles.cockpitPage]: selectedSection === 'cockpit' })}
        data-help-panel-host
      >
        <div className={styles.sections}>
          <ManagerPlanningCockpitSwitcher
            selectedSection={selectedSection}
            sections={sections}
            myPartnersCount={myPartnersCount}
            onChange={setSelectedSection}
          />

          <div className={styles.sectionContent}>
            {selectedSection === 'cockpit' && (
              <div className={styles.root}>
                <div
                  id={sidebarId}
                  className={cn(styles.sidebar, { [styles.sidebarFolded]: isSidebarFolded })}
                >
                  <div className={styles.filters} data-tour-id={TOUR_TARGETS.filterPanel}>
                    {COCKPIT_UNIFIED_FILTER_PANEL_ENABLED ? (
                      <CockpitUnifiedFilterPanel />
                    ) : (
                      <>
                        <CockpitRtm />
                        <CockpitFilterPanel />
                      </>
                    )}
                  </div>
                </div>

                {hasNoFilteredData ? (
                  <ManagerCockpitNoFilteredData />
                ) : (
                  <div className={styles.dashboard}>
                    {/* The tour starts from the Help & tools panel; isTourHostVisible still
                          holds auto-start back until the cockpit is on screen. */}
                    <ManagerCockpitScopePanel
                      isTileExpanded={expandedTileId !== null}
                      sidebarToggle={{
                        isOpen: !isSidebarFolded,
                        controls: sidebarId,
                        onToggle: () => setIsSidebarHidden(!isSidebarFolded),
                      }}
                      tour={{
                        isAvailable: !isTourLoading && hasTours,
                        isActive: isTourActive,
                        onStart: startTour,
                        labelKey: MANAGER_COCKPIT_TOUR_TRANSLATION_KEYS.tourButton,
                      }}
                    >
                      <CockpitExportProvider>
                        <TileBoard
                          expandAnimation="stretch"
                          tiles={tiles}
                          columns={4}
                          rowSizes={MANAGER_COCKPIT_ROW_SIZES}
                          fitHeight
                          expandedTileId={expandedTileId}
                          onExpandedTileChange={setExpandedTileId}
                        />
                      </CockpitExportProvider>
                    </ManagerCockpitScopePanel>
                  </div>
                )}
              </div>
            )}

            {selectedSection === 'topPartners' && (
              <TopPartnersManagement
                fYear={fYear}
                partnerFlags={partnerFlags}
                partners={topPartners}
                setPartners={setTopPartners}
                approvers={partnerFlagApprovers}
                pbmAssignments={pbmAssignments}
                formKey="MANAGER_PLANNING_COCKPIT_TOP_PARTNERS_FORM"
              />
            )}

            {selectedSection === 'myPartners' && (
              <ManagerCockpitMyPartners
                isLoading={isMyPartnersLoading}
                onPartnersChanged={handleMyPartnersChanged}
              />
            )}

            {selectedSection === 'businessPriorities' && <BusinessPriorities />}

            {selectedSection === 'marketYoyTargets' && <MarketYoyGrowthExpectation />}

            {selectedSection === 'strategicInitiatives' && <StrategicInitiatives />}

            {selectedSection === 'coverageReport' && <CoverageReport fYear={fYear} />}
          </div>
        </div>
      </div>
    </PlanningCockpitContentWrapper>
  )
}

export const ManagerPlanningCockpit = () => (
  <NetworkDebugSource name="Manager cockpit">
    <WidgetTourProvider>
      <ManagerPlanningCockpitBody />
    </WidgetTourProvider>
  </NetworkDebugSource>
)

ManagerPlanningCockpit.displayName = 'ManagerPlanningCockpitWidget'
